import {
    Bytes,
    type CollectionReference,
    type DocumentReference,
    type FieldValue,
    type Firestore,
    serverTimestamp as firestoreServerTimestamp,
    GeoPoint,
    Timestamp,
} from 'firebase/firestore';

import { numericCompare } from './Sort';

export function serverTimestamp(): Timestamp | FieldValue {
    return firestoreServerTimestamp();
}

export function nowTimestamp(): Timestamp {
    return Timestamp.now();
}

export function nowOrServerTimestamp(): Timestamp | FieldValue {
    return nowTimestamp();
}

export function nullTimestamp(): Timestamp {
    return new Timestamp(NaN, NaN);
}

export function nullOrServerTimestamp(): Timestamp | FieldValue {
    return nullTimestamp();
}

export interface TimestampLike {
    readonly seconds: number;
    readonly nanoseconds: number;
}

export function cloneTimestamp(v: TimestampLike): Timestamp {
    return new Timestamp(v.seconds, v.nanoseconds);
}

export function isTimestampLike(x: unknown): x is TimestampLike {
    if (
        x != null &&
        typeof x === 'object' &&
        'seconds' in x &&
        'nanoseconds' in x
    ) {
        return (
            typeof x.seconds === 'number' && typeof x.nanoseconds === 'number'
        );
    }
    return false;
}

function sameValueZero(a: number, b: number): boolean {
    return a === b || (isNaN(a) && isNaN(b));
}

export function areTimestampsEqual(a: TimestampLike, b: TimestampLike) {
    return (
        sameValueZero(a.seconds, b.seconds) &&
        sameValueZero(a.nanoseconds, b.nanoseconds)
    );
}

export function timestampCompare(a: TimestampLike, b: TimestampLike): number {
    const secondsOrder = numericCompare(a.seconds, b.seconds);
    return secondsOrder === 0
        ? numericCompare(a.nanoseconds, b.nanoseconds)
        : secondsOrder;
}

export function timestampToDate(timestamp: Timestamp | TimestampLike) {
    if ('toDate' in timestamp && typeof timestamp.toDate === 'function') {
        return timestamp.toDate();
    }

    return new Timestamp(timestamp.seconds, timestamp.nanoseconds).toDate();
}

export interface GeoPointLike {
    readonly latitude: number;
    readonly longitude: number;
}

export function cloneGeoPoint(v: GeoPointLike): GeoPoint {
    return new GeoPoint(v.latitude, v.longitude);
}

export function isGeoPointLike(x: unknown): x is GeoPointLike {
    if (
        x != null &&
        typeof x === 'object' &&
        'latitude' in x &&
        'longitude' in x
    ) {
        return (
            typeof x.latitude === 'number' && typeof x.longitude === 'number'
        );
    }
    return false;
}

export function areGeoPointsEqual(a: GeoPointLike, b: GeoPointLike) {
    return (
        sameValueZero(a.latitude, b.latitude) &&
        sameValueZero(a.longitude, b.longitude)
    );
}

export function geoPointCompare(a: GeoPointLike, b: GeoPointLike): number {
    const latOrder = numericCompare(a.latitude, b.latitude);
    return latOrder === 0 ? numericCompare(a.longitude, b.longitude) : latOrder;
}

export interface BytesLike {
    isEqual(other: BytesLike): boolean;
    toBase64(): string;
    toString(): string;
    toUint8Array(): Uint8Array;
}

export function isBytesLike(x: unknown): x is BytesLike {
    if (x != null && typeof x === 'object') {
        const y = x as any;
        return (
            typeof y.isEqual === 'function' &&
            typeof y.toBase64 === 'function' &&
            typeof y.toJSON === 'function' &&
            typeof y.toUint8Array === 'function'
        );
    }
    return false;
}

export function cloneBytes(b: BytesLike): Bytes {
    return Bytes.fromUint8Array(b.toUint8Array());
}

// Warning: isFirestoreFieldValue is unique among these runtime check
//  functions in that it depends on private implementation details, as
//  there isn't enough publicly documented to reliably determine a
//  FieldValue (although instanceof is an option now that the Firebase
//  libraries are less subject to state conflict)
//
//  Upshot is: recheck it when upgrading the Firestore client deps.

export function isFirestoreFieldValue(x: unknown): x is FieldValue {
    return (
        x != null &&
        typeof x === 'object' &&
        '_methodName' in x &&
        typeof x._methodName === 'string' &&
        'isEqual' in x &&
        typeof x.isEqual === 'function'
    );
}

export function isFirestore(x: unknown): x is Firestore {
    return (
        x != null &&
        typeof x === 'object' &&
        'type' in x &&
        (x.type === 'firestore' || x.type === 'firestore-lite') &&
        'app' in x &&
        typeof x.app === 'object' &&
        x.app != null
    );
}

export function isFirestoreReference(
    x: unknown
): x is DocumentReference | CollectionReference {
    return (
        x != null &&
        typeof x === 'object' &&
        'type' in x &&
        (x.type === 'document' || x.type === 'collection') &&
        'path' in x &&
        typeof x.path === 'string' &&
        'firestore' in x &&
        isFirestore(x.firestore)
    );
}
